import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PersonSearch,
  Edit,
  DeleteOutline,
} from '@mui/icons-material';
import ProveedorInlineSearchField from './ProveedorInlineSearchField';
import ProveedorSearch from './ProveedorSearch';
import { Proveedor } from '../services/proveedorService';

const TIPO_IVA_LABELS: Record<string, string> = {
  I: 'Inscripto',
  N: 'No Inscripto',
  E: 'Exento',
  M: 'Monotributo',
  RI: 'Resp. Inscripto',
  CF: 'Consumidor Final',
  EX: 'Exento',
  MO: 'Monotributista',
  NI: 'No Inscripto',
};

/** Altura del input outlined mediano de MUI, para alinear el botón lateral (desktop). */
const INPUT_HEIGHT = 56;

export interface ProveedorSelectorValue {
  nroProveedor: number;
  nombre?: string;
  cuit?: string;
  tipoIva?: string;
}

export interface ProveedorSelectorFieldProps {
  value: ProveedorSelectorValue | null;
  onSelect: (proveedor: Proveedor) => void;
  onClear: () => void;
  /** Si se informa, muestra el lápiz para editar nombre/CUIT/tipo IVA del comprobante. */
  onEditDatosFiscales?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  label?: string;
  required?: boolean;
}

const ProveedorSelectorField: React.FC<ProveedorSelectorFieldProps> = ({
  value,
  onSelect,
  onClear,
  onEditDatosFiscales,
  disabled = false,
  disabledMessage,
  label = 'Proveedor',
  required = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openProveedorSearch, setOpenProveedorSearch] = useState(false);

  const hasProveedor = Boolean(value && value.nroProveedor > 0 && value.nombre);

  const handleSelect = (proveedor: Proveedor) => {
    onSelect(proveedor);
    setOpenProveedorSearch(false);
  };

  const handleInlineSelect = (proveedor: { nroProveedor: number; nombre?: string }) => {
    handleSelect(proveedor as Proveedor);
  };

  const fieldLabel = (
    <Typography
      variant="caption"
      color="text.secondary"
      component="label"
      sx={{
        display: 'block',
        ml: '14px',
        mb: 0.5,
        fontSize: '0.75rem',
        lineHeight: 1,
      }}
    >
      {label}
      {required ? (
        <Box component="span" sx={{ color: 'error.main' }}>
          {' *'}
        </Box>
      ) : null}
    </Typography>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {!hasProveedor ? (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <ProveedorInlineSearchField
              label={label}
              required={required}
              placeholder="Nº proveedor, nombre o CUIT"
              helperText=""
              disabled={disabled}
              onSelect={handleInlineSelect}
            />
          </Box>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            startIcon={<PersonSearch color="primary" />}
            disabled={disabled}
            fullWidth={isMobile}
            onClick={() => setOpenProveedorSearch(true)}
            sx={{
              height: { xs: 'auto', md: INPUT_HEIGHT },
              py: { xs: 1.25, md: 0 },
              px: 2,
              borderRadius: 2,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderColor: 'divider',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'primary.main',
              },
            }}
          >
            Seleccionar proveedor
          </Button>
        </Stack>
      ) : (
        <Box>
          {fieldLabel}
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              minHeight: INPUT_HEIGHT,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <CardContent sx={{ py: 1, width: '100%', '&:last-child': { pb: 1 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Box flex={1} minWidth={0}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ wordBreak: 'break-word', whiteSpace: { xs: 'normal', sm: 'nowrap' } }}
                    >
                      {value!.nombre}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      #{value!.nroProveedor}
                    </Typography>
                    {value!.cuit ? (
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        CUIT: {value!.cuit}
                      </Typography>
                    ) : null}
                    {value!.tipoIva ? (
                      <Chip
                        label={TIPO_IVA_LABELS[value!.tipoIva] ?? value!.tipoIva}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    ) : null}
                  </Stack>
                </Box>
                {!disabled ? (
                  <Stack direction="row" spacing={0} flexShrink={0} alignItems="center">
                    <Tooltip title="Cambiar proveedor">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => setOpenProveedorSearch(true)}
                        aria-label="Cambiar proveedor"
                      >
                        <PersonSearch fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {onEditDatosFiscales ? (
                      <Tooltip title="Editar nombre, CUIT y tipo IVA">
                        <IconButton
                          size="small"
                          onClick={onEditDatosFiscales}
                          aria-label="Editar nombre, CUIT y tipo IVA"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Quitar proveedor">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={onClear}
                        aria-label="Quitar proveedor"
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}
      {disabled && disabledMessage ? (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
          {disabledMessage}
        </Typography>
      ) : null}
      <ProveedorSearch
        open={openProveedorSearch}
        onClose={() => setOpenProveedorSearch(false)}
        onSelect={handleSelect}
      />
    </Box>
  );
};

export default ProveedorSelectorField;
